import React, { memo, useCallback } from "react";

import {
  Avatar,
  Button,
  Divider,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";

import colors from "app/colors";
import { useOverlay } from "modules/providers/overlay-provider";
import {
  CopyIcon,
  MailIcon,
  PersonIcon,
  SyncoInvitePeopleIcon,
} from "modules/icons";
import {
  InviteFragment,
  InvitesQuery,
  InvitesQueryVariables,
  InviteType,
} from "gql/graphql";
import useInvite from "modules/hooks/use-invite";
import { useOrganization } from "modules/providers/organization-provider";
import Toast from "modules/toasts/toast";
import { useQuery } from "@apollo/client";
import INVITES_QUERY from "modules/hooks/_networking/invites";
import { Virtuoso } from "react-virtuoso";
import { formatPhoneNumber, getInviteLink } from "modules/utils/Functions";
import useDisplayToast from "modules/hooks/use-display-toast";

const InviteModal = () => {
  const { organization } = useOrganization();
  const { closeModal } = useOverlay();
  const { displayToast } = useDisplayToast();
  const { createInviteAPI } = useInvite();

  const { data } = useQuery<InvitesQuery, InvitesQueryVariables>(INVITES_QUERY);

  const invites = (data?.invites || []).filter(
    (invite) => invite.type === InviteType.User,
  );

  const onCreateInviteClicked = useCallback(async () => {
    if (!organization) {
      return;
    }
    const { data: inviteData } = await createInviteAPI({
      variables: {
        input: {
          organizationId: organization.id,
          type: InviteType.Organization,
        },
      },
    });
    if (!inviteData?.createInvite) {
      return;
    }
    navigator.clipboard.writeText(
      getInviteLink(origin, inviteData.createInvite.id),
    );
    displayToast(() => (
      <Toast
        message="Invite link copied!"
        leftIcon={<CopyIcon boxSize={5} fill={colors.grayMain} />}
      />
    ));
  }, [createInviteAPI, organization, displayToast]);

  const onResendClicked = useCallback(
    (invite: InviteFragment) => {
      if (!organization) {
        return;
      }
      const subject = `Join ${organization.name} on Synco`;
      const body = ` ${invite.firstName ? `Hi ${invite.firstName}` : "Hi!"} I'm inviting you to join the ${organization.name} team on Synco, the new app we use to message and stay organized. Use this link to sign up: ${getInviteLink(origin, invite.id)}`;
      const mailtoLink = `mailto:${invite.email ? encodeURIComponent(invite.email) : ""}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = mailtoLink;
    },
    [organization],
  );

  const onInviteViaEmailClicked = useCallback(async () => {
    if (!organization) {
      return;
    }
    const { data: inviteData } = await createInviteAPI({
      variables: {
        input: {
          organizationId: organization.id,
          type: InviteType.Organization,
        },
      },
    });
    if (!inviteData?.createInvite) {
      return;
    }
    const invite = inviteData.createInvite;
    onResendClicked(invite);
  }, [createInviteAPI, onResendClicked, organization]);

  const renderInvite = useCallback(
    (_: number, invite: InviteFragment) => {
      const hasName = invite.firstName || invite.lastName;
      let inviteState: "joined" | "email" | null = null;
      if (InviteType.User === invite.type && invite.userIdList.length > 0) {
        inviteState = "joined";
      } else if (invite.email) {
        inviteState = "email";
      }
      return (
        <HStack
          w="full"
          h="56px"
          borderBottomWidth={1}
          borderBottomColor={colors.gray0}
        >
          <Avatar
            size="sm"
            bg={colors.grayPrimary}
            icon={<PersonIcon fill={colors.white} />}
          />
          {hasName ? (
            <VStack spacing={0} alignItems="flex-start" w="full">
              <Text
                fontWeight={700}
                fontSize={16}
                color={colors.blackSecondary}
              >
                {invite.firstName} {invite.lastName}
              </Text>
              <Text fontWeight={400} fontSize={12} color={colors.gray50}>
                <VStack spacing={0} alignItems="flex-start" w="full">
                  <Text>{invite.email}</Text>
                  <Text>{formatPhoneNumber(invite.phone)}</Text>
                </VStack>
              </Text>
            </VStack>
          ) : (
            <VStack spacing={0} alignItems="flex-start" w="full">
              <Text
                fontWeight={700}
                fontSize={16}
                color={colors.blackSecondary}
              >
                <VStack spacing={0} alignItems="flex-start" w="full">
                  <Text>{invite.email}</Text>
                  <Text>{formatPhoneNumber(invite.phone)}</Text>
                </VStack>
              </Text>
            </VStack>
          )}
          {inviteState === "joined" && (
            <Text fontWeight={700} fontSize={11} color={colors.gray50}>
              Joined
            </Text>
          )}
          {inviteState === "email" && (
            <Button
              variant="hover"
              bg={colors.blueLight}
              size="xs"
              fontSize={11}
              fontWeight={700}
              color={colors.blue}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                e.preventDefault();
                onResendClicked(invite);
              }}
            >
              Resend
            </Button>
          )}
        </HStack>
      );
    },
    [onResendClicked],
  );

  if (!organization) {
    return null;
  }

  return (
    <Modal isOpen onClose={closeModal} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalCloseButton left={5} top={6} color={colors.gray40} />
        <ModalBody>
          <VStack w="full" spacing={4} py={5} px={6}>
            <Text color={colors.blackSecondary} fontSize={18} fontWeight={700}>
              Invitations
            </Text>
            <SyncoInvitePeopleIcon />
            <VStack spacing={0}>
              <Text
                fontFamily="Outfit"
                color={colors.black}
                fontSize={24}
                fontWeight={700}
              >
                Invite Teammates!
              </Text>
              <Text color={colors.grayMain} fontSize={16} fontWeight={500}>
                Invite others at {organization.name}.
              </Text>
            </VStack>
            <Button
              h="36px"
              w="full"
              variant="hover"
              alignSelf="center"
              onClick={onCreateInviteClicked}
              fontWeight={700}
              fontSize={16}
              fontFamily="Outfit"
              color={colors.white}
              bg={colors.blue}
              borderRadius="full"
            >
              <HStack>
                <CopyIcon boxSize={5} fill={colors.white} />
                <Text>Copy Invite Link</Text>
              </HStack>
            </Button>
            <Button
              h="36px"
              w="full"
              variant="hover"
              alignSelf="center"
              onClick={onInviteViaEmailClicked}
              fontWeight={700}
              fontSize={16}
              fontFamily="Outfit"
              color={colors.white}
              bg={colors.blue}
              borderRadius="full"
            >
              <HStack>
                <MailIcon boxSize={5} fill={colors.white} />
                <Text>Invite Via Email</Text>
              </HStack>
            </Button>
            {invites.length > 0 && (
              <VStack w="full" alignItems="flex-start" h={400} spacing={0}>
                <Text
                  fontSize={16}
                  fontWeight={600}
                  color={colors.blackSecondary}
                >
                  Invitations ({invites.length})
                </Text>
                <Divider />
                <Virtuoso
                  data={invites}
                  defaultItemHeight={56}
                  itemContent={renderInvite}
                  style={{ width: "100%", height: "100%" }}
                />
              </VStack>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(InviteModal);
